import { T } from '../data/tokens';
import FlatIcon from './FlatIcon';
import Gallery from './Gallery';
import Accordion from './Accordion';

const accent  = '#c4a0ff';
const accentR = '196,160,255';

const SCREENS = [
  { label: 'Widget Framework-a',  src: 'images/rocket-widget-framework-a.png',  bg: '#F5F3FF', fg: '#7c3aed' },
  { label: 'Widget Framework-b',  src: 'images/rocket-widget-framework-b.png',  bg: '#FAF5FF', fg: '#9333ea' },
  { label: 'Widget Framework-f',  src: 'images/rocket-widget-framework-f.png',  bg: '#F0F9FF', fg: '#0369a1' },
  { label: 'Widget Framework-c',  src: 'images/rocket-widget-framework-c.png',  bg: '#FDF4FF', fg: '#a21caf' },
  { label: 'Widget Framework-d',  src: 'images/rocket-widget-framework-d.png',  bg: '#F0F9FF', fg: '#0369a1' }
];

const IMPACT_GROUPS = [
  { groupLabel: 'Developer Productivity', groupIcon: 'bolt', stats: [
    { num: '65%',  desc: 'Faster App Development',    sub: 'Enterprise teams post-launch' },
    { num: '3x',   desc: 'Faster Component Reuse',    sub: 'Via widget framework' },
    { num: '82',   desc: 'SUS Score',                 sub: 'Up from 54 (legacy UI)' },
  ]},
  { groupLabel: 'Cost & Commercial Impact', groupIcon: 'monetization', stats: [
    { num: '$50K', desc: 'Licensing Eliminated',      sub: 'Per client per year' },
    { num: '3',    desc: 'Product Lines Adopted',     sub: 'Design system rollout' },
    { num: '$2.3M', desc: 'Annual Savings Delivered', sub: 'Operational efficiency' },
  ]},
  { groupLabel: 'Adoption & Quality', groupIcon: 'target', stats: [
    { num: '200+', desc: 'Components Standardised',   sub: 'Across entire platform' },
    { num: '0',    desc: 'Veteran User Churn',        sub: 'During modernisation' },
    { num: 'AA',   desc: 'WCAG Compliance',           sub: 'Full 2.1 accessibility' },
  ]},
];

export default function CaseStudyRocket() {
  return (
    <div>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #1a0533 0%, #3b1068 50%, #764ba2 100%)', color: 'white', padding: '3.5rem 3rem 3rem', position: 'relative', overflow: 'hidden' }} className="cs-hero">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(${accentR},0.1) 1px,transparent 1px),linear-gradient(90deg,rgba(${accentR},0.1) 1px,transparent 1px)`, backgroundSize: '48px 48px', pointerEvents: 'none' }} />
        <div style={T.heroInner}>
          <span style={{ ...T.pill, background: `rgba(${accentR},0.25)`, border: `1px solid rgba(${accentR},0.5)`, color: '#e8d5ff' }}>Design System · Enterprise UX · Low-Code Platform</span>
          <h2 style={T.heroTitle}>Rocket Uniface UX</h2>
          <p style={T.heroSub}>Rocket Software — Modernising a 40-Year-Old Enterprise Platform</p>
          <p style={{ ...T.heroTagline, color: accent }}>65% Faster Dev Cycles &amp; $15K–$50K Licensing Eliminated Per Client</p>
          <div style={{ ...T.resultBand, borderLeft: '4px solid #c4a0ff' }}>
            <p style={T.resultText}>
              I led the UX modernisation of Rocket Uniface — a mission-critical enterprise platform used by global banks, insurers, and government agencies for 40 years. The challenge was to adopt <strong>Microsoft Fluent Design System</strong> and build a custom widget framework that satisfied both veteran power users who had decades of muscle memory and new developers who expected modern tooling.
            </p>
          </div>
          <div style={T.metricsRow} className="cs-metrics">
            <div style={T.metric}><span style={T.metricNum}>65%</span><span style={T.metricLabel}>Faster Dev Cycles</span><span style={T.metricSub}>For enterprise teams post-launch</span></div>
            <div style={T.metric}><span style={T.metricNum}>$50K</span><span style={T.metricLabel}>Licensing Saved</span><span style={T.metricSub}>Per client per year</span></div>
            <div style={T.metric}><span style={T.metricNum}>3</span><span style={T.metricLabel}>Product Lines</span><span style={T.metricSub}>Adopted the design system</span></div>
            <div style={T.metric}><span style={T.metricNum}>40yr</span><span style={T.metricLabel}>Platform Legacy</span><span style={T.metricSub}>Modernised without disruption</span></div>
          </div>
        </div>
      </div>

      {/* ── Stats Strip ─────────────────────────────────────────────── */}
      <div style={T.detailStatsStrip} className="detail-stats-strip">
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Role</span><span style={T.detailStatCellValue}>Senior UX Designer P4 / Product design Lead</span></div>
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Duration</span><span style={T.detailStatCellValue}>Ongoing (2021 – Present)</span></div>
        <div style={{ ...T.detailStatCell }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Team</span><span style={T.detailStatCellValue}>Rocket Software — Global Team</span></div>
      </div>

      {/* ── Problem ─────────────────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="help" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Problem</p>
        <h3 style={T.sectionTitle}>A 40-year-old platform at a UX crossroads</h3>
        <p style={T.sectionIntro}>Rocket Uniface powered mission-critical applications at global banks, insurers and government agencies — but its UI was decades behind modern expectations. Veteran power users had built decades of muscle memory into the old interface. Any redesign that broke those patterns risked catastrophic rejection from the existing customer base.</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '1.25rem' }}>
          {[
            { v: '40 yrs', l: 'platform legacy' },
            { v: '54',     l: 'NPS score (legacy UI)' },
            { v: '$50K',   l: 'third-party UI cost/client' },
          ].map((s) => (
            <div key={s.v} style={{ flex: 1, borderRadius: '10px', padding: '1rem 1.25rem', background: `rgba(${accentR},0.06)`, border: `1px solid rgba(${accentR},0.2)` }}>
              <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1.7rem', color: accent, lineHeight: 1, marginBottom: '4px' }}>{s.v}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', color: '#64748b', letterSpacing: '0.04em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Solution — 5 cards ──────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="rocket" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Final Solution</p>
        <h3 style={T.sectionTitle}>What We Built</h3>
        <p style={T.sectionIntro}>A Fluent Design-based UX system and extensible widget framework that lets enterprise organisations build fully branded, production-grade applications inside Rocket Uniface — without writing UI code or paying for third-party component libraries.</p>
        <div style={T.solCards}>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>01</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Fluent Design System Foundation <span style={{ ...T.solTag, background: `rgba(${accentR},0.12)`, color: accent }}>Core Achievement</span></h4>
              <p style={T.solBody}>Adopted Microsoft Fluent Design System as the visual language for the entire platform — standardising typography, colour tokens, spacing, motion, and elevation rules across all Uniface UI surfaces.</p>
              <div style={{ ...T.solResult, color: accent, background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)` }}>Result: Eliminated visual inconsistency across 200+ legacy UI components</div>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>02</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Custom Widget Framework</h4>
              <p style={T.solBody}>A composable component architecture that allows organisations to create their own branded widgets — extending the platform with custom UI blocks without requiring engineering support or platform source code access.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>03</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Progressive Disclosure Navigation</h4>
              <p style={T.solBody}>Redesigned the platform navigation to reveal advanced features progressively — protecting the mental models of veteran users while surfacing modern workflows naturally for new developers joining the platform.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>04</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>AI-Assisted Development Interface</h4>
              <p style={T.solBody}>Integrated AI code suggestions and contextual help directly into the low-code editor — reducing the learning curve for new developers and accelerating application build time for experienced teams.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>05</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Unified Component Documentation Hub</h4>
              <p style={T.solBody}>A living design system documentation site — spec sheets, usage guidelines, code snippets, and Figma tokens — enabling distributed global teams to build consistently without centralised design reviews.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Gallery ─────────────────────────────────────────────────── */}
      <Gallery screens={SCREENS} accent={accent} />

      {/* ── Design Strategy ─────────────────────────────────────────── */}
      <Accordion label="🎯 The Design Strategy" accent={accent}>
        <div style={{ ...T.section, background: '#120a1e' }} className="cs-section">
          <h3 style={{ ...T.sectionTitle, color: '#fff' }}>Modernising Without Breaking Trust</h3>
          <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.8)' }}>Rocket Uniface customers had built entire banking and insurance systems over decades. A wrong move could destroy years of user trust and trigger enterprise churn.</p>
          <div style={T.strategyGrid} className="cs-strategy-grid">
            <div style={T.strategyBlock}>
              <h4 style={{ ...T.strategyHeading, color: accent }}>The Strategic Pivot</h4>
              <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.75)', marginTop: '0.5rem', marginBottom: 0 }}>
                Early proposals called for a complete UI overhaul. Research revealed this would alienate power users who had decades of keyboard shortcuts, mental models, and macros built around the existing interface. The pivot: <strong>Additive Modernisation</strong> — layer the new design system on top of existing patterns, never removing, always extending.
              </p>
            </div>
            <div style={T.strategyBlock}>
              <h4 style={{ ...T.strategyHeading, color: accent }}>The Critical Trade-off</h4>
              <div style={T.tradeoffItem}>
                <span style={{ ...T.tradeoffLabel, color: accent }}>The Tension</span>
                <span style={T.tradeoffText}>New developers expected a modern, visual-first low-code experience. Veterans expected every existing keyboard shortcut and workflow to remain exactly as it was.</span>
              </div>
              <div style={{ ...T.tradeoffItem, marginTop: '0.9rem' }}>
                <span style={{ ...T.tradeoffLabel, color: accent }}>The Decision</span>
                <span style={T.tradeoffText}>Dual interaction layers — a new visual component panel for discovery-based development, with the full legacy keyboard-driven interface preserved and accessible at all times beneath it.</span>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Journey ─────────────────────────────────────────────────── */}
      <Accordion label="🔍 The Journey" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>How We Got There</h3>
          <div style={T.journeyGrid} className="cs-journey-grid">

            {/* Challenge card */}
            <div style={T.journeyCard}>
              <div style={T.journeyIcon}><FlatIcon name="warning" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Challenge &amp; Pain Points</h4>
              <p style={T.sectionIntro}>Uniface was losing enterprise deals to modern low-code competitors. New developers found it intimidating and dated. But existing customers — global banks and insurers — could not afford disruption to mission-critical workflows.</p>
              {[
                { q: 'I have used this platform for 22 years. Every time they change something I lose a week of productivity relearning workflows my hands know by reflex.', who: '— Senior Enterprise Developer, Banking' },
                { q: 'We evaluated Uniface against three competitors. The interface looked like it was from 2005. Our junior devs refused to learn it.',                     who: '— IT Director, Insurance Company' },
                { q: 'We pay three different UI licensing fees on top of the Uniface license. Our CTO wants them gone. We need this to do it natively.',                   who: '— Enterprise Architect, Government Agency' },
              ].map((q) => (
                <div key={q.who} style={{ ...T.quote, borderLeft: '3px solid #c4a0ff' }}>
                  <p style={T.quoteText}>{q.q}</p>
                  <cite style={{ ...T.quoteCite, color: accent }}>{q.who}</cite>
                </div>
              ))}
            </div>

            {/* Aha Moment card */}
            <div style={{ ...T.journeyCard, background: 'rgba(196,160,255,0.04)', border: '1px solid rgba(196,160,255,0.15)' }}>
              <div style={T.journeyIcon}><FlatIcon name="lightbulb" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Aha Moment</h4>
              <p style={T.sectionIntro}>After shadowing a veteran developer, I watched them navigate using only keyboard shortcuts — never touching the mouse — at extraordinary speed. A full visual redesign would have destroyed this entirely.</p>
              <p style={{ ...T.sectionIntro, marginTop: '0.75rem' }}>
                The insight: <strong>veteran users and new users were using completely different mental models of the same product.</strong> The solution was not to pick one — it was to design an interface that served both simultaneously, with neither group aware of the compromise.
              </p>
            </div>
          </div>

          {/* Research Methods */}
          <h4 style={T.subsectionTitle}>Research Methods</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'mic', title: 'Stakeholder Interviews',  desc: '31 interviews across enterprise clients, developers, and product leadership across 6 countries' },
              { icon: 'visibility', title: 'Contextual Inquiry',       desc: 'Shadowed 14 developers — both veterans and new hires — during live application development sessions' },
              { icon: 'check_circle', title: 'Competitive Teardowns',    desc: 'Analysed OutSystems, Mendix, and ServiceNow to identify the modern patterns customers expected' },
              { icon: 'clipboard', title: 'Component Audit',          desc: 'Full audit of 200+ existing UI components to identify consolidation and modernisation opportunities' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Key Insights */}
          <h4 style={T.subsectionTitle}>Key Insights</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'memory', title: 'Legacy is a Feature, Not a Bug',         desc: 'For veteran users, decades of muscle memory represent enormous value. Disrupting it is not modernisation — it is destruction of a competitive advantage.' },
              { icon: 'palette', title: 'Visual Polish Drives Procurement',        desc: 'Enterprise procurement decisions are made by IT directors and CTOs who judge on first impressions. Modern aesthetics directly influenced deal outcomes.' },
              { icon: 'extension', title: 'Extensibility Over Completeness',         desc: 'Customers wanted to build their own branded components. A closed system — however polished — would still require expensive third-party licensing.' },
              { icon: 'globe', title: 'Global Teams Need Async Design Alignment', desc: 'With developers across 12 time zones, a shared living documentation system was more valuable than any design review process.' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* 4 Pillars */}
          <h4 style={T.subsectionTitle}>4 Pillars of the Design System</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'hub', title: 'Consistency',    desc: 'Every token, component, and pattern standardised across all product lines — one source of truth.' },
              { icon: 'extension', title: 'Extensibility',  desc: 'The system was built to be extended, not forked — customers add to it without breaking the foundation.' },
              { icon: 'accessible', title: 'Accessibility',  desc: 'WCAG 2.1 AA compliance built in at token level — not retrofitted as an afterthought.' },
              { icon: 'bolt', title: 'Performance',    desc: 'Every component optimised for Uniface runtime — no visual system that looked good in Figma but degraded in production.' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testing & Iteration */}
          <h4 style={T.subsectionTitle}>Testing &amp; Iteration</h4>
          <div style={T.iterTimeline} className="cs-iter-timeline">
            <div style={{ ...T.iterItem, background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#ef4444' }} />
              <div>
                <strong style={T.iterLabel}>Phase 1</strong>
                <p style={T.iterText}>Full visual overhaul proposed — <span style={{ color: '#ef4444', fontWeight: 600 }}>veteran users rejected it entirely</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(251,146,60,0.06)', borderColor: 'rgba(251,146,60,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#f97316' }} />
              <div>
                <strong style={T.iterLabel}>Phase 2</strong>
                <p style={T.iterText}>Fluent skin on top of legacy layout — <span style={{ color: '#f97316', fontWeight: 600 }}>new users still found it confusing</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(196,160,255,0.08)', borderColor: 'rgba(196,160,255,0.3)' }}>
              <span style={{ ...T.iterDot, background: '#c4a0ff' }} />
              <div>
                <strong style={T.iterLabel}>Phase 3 (Final)</strong>
                <p style={T.iterText}>Dual interaction layers with shared Fluent tokens — <span style={{ color: '#c4a0ff', fontWeight: 600 }}>both user groups satisfied</span></p>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Design Process ──────────────────────────────────────────── */}
      <Accordion label="✏️ Design Process" accent={accent}>
        <div style={{ ...T.section, background: '#fafaf8' }} className="cs-section">
          <h3 style={T.sectionTitle}>The Messy Middle</h3>
          <p style={T.sectionIntro}>Before the dual interaction layers, there were two rejected overhauls and one stakeholder confrontation that nearly ended the project entirely.</p>

          {/* What I Believed — red callout box */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid #c4a0ff`, borderRadius: '10px', padding: '1.5rem 1.75rem', marginBottom: '1.5rem', marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#c4a0ff', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 600 }}>What I Believed at the Start</p>
            <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.45, marginBottom: '0.75rem' }}>
              "A modern redesign will win over even the most resistant veteran users once they see how much better it looks."
            </p>
            <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
              This was my initial assumption and the internal stakeholder expectation. We designed a comprehensive Fluent-based overhaul. Clean, modern, beautifully consistent. The first veteran user test lasted 11 minutes before the participant stood up and said they would never use it.
            </p>
          </div>

          {/* 3 Modernisation Approaches — 3-col grid */}
          <h4 style={T.subsectionTitle}>3 Modernisation Approaches — Only One Survived</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }} className="cs-three-col">
            {[
              { dot: '#ef4444', border: '#fecaca', label: 'PHASE 1 — REJECTED', title: 'Full Visual Overhaul',          body: 'Complete Fluent redesign — new navigation, new component layout, new interaction patterns. Veteran users rejected it entirely. 22 years of muscle memory destroyed in one update.' },
              { dot: '#f97316', border: '#fed7aa', label: 'PHASE 2 — PARTIAL',  title: 'Fluent Skin on Legacy Layout',  body: 'Applied Fluent visual tokens to the existing layout without changing structure. Veterans were comfortable — but new users still found the navigation confusing and dated.' },
              { dot: '#22c55e', border: '#bbf7d0', label: 'PHASE 3 — SHIPPED',  title: 'Dual Interaction Layers',        body: 'Legacy keyboard interface preserved exactly. New visual component panel layered on top. Both user groups use the same product — neither is aware of the compromise.' },
            ].map((v) => (
              <div key={v.label} style={{ background: '#fff', border: `1px solid ${v.border}`, borderRadius: '10px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: v.dot, display: 'inline-block' }} />
                  <strong style={{ fontSize: '0.78rem', color: v.dot, fontFamily: "'DM Mono', monospace", letterSpacing: '0.04em' }}>{v.label}</strong>
                </div>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.5rem' }}>{v.title}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{v.body}</p>
              </div>
            ))}
          </div>

          {/* Widget Naming Convention — 4-col grid */}
          <h4 style={T.subsectionTitle}>How the Widget Framework Naming Convention Was Fixed</h4>
          <p style={{ ...T.sectionIntro, marginBottom: '1.25rem' }}>The framework worked technically — but engineers refused to use it because the naming felt alien. It took 4 rounds to get language veterans would adopt.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '2rem' }} className="cs-four-col">
            {[
              { v: 'V1', label: 'Abstract design tokens',       result: 'Ignored',           color: '#ef4444', note: 'color-primary-500 meant nothing to engineers used to legacy property names' },
              { v: 'V2', label: 'Fluent standard naming',        result: 'Partial adoption',  color: '#f97316', note: 'Microsoft naming felt foreign to a Uniface-native team' },
              { v: 'V3', label: 'Hybrid legacy-Fluent names',   result: 'Confused',          color: '#eab308', note: 'Two naming systems running in parallel created inconsistency' },
              { v: 'V4', label: 'Veteran-led token naming',     result: 'Full adoption',     color: '#22c55e', note: 'Veteran developers renamed the tokens in their own language. Adoption went from 30% to 94%.' },
            ].map((e) => (
              <div key={e.v} style={{ background: '#fff', border: `1px solid ${e.color}44`, borderTop: `3px solid ${e.color}`, borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.68rem', fontFamily: "'DM Mono', monospace", color: e.color, fontWeight: 700, marginBottom: '0.4rem', letterSpacing: '0.06em' }}>{e.v}</div>
                <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.35rem', lineHeight: 1.35 }}>{e.label}</p>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: e.color, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.4rem' }}>{e.result}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{e.note}</p>
              </div>
            ))}
          </div>

          {/* The Decision — dark bg box */}
          <div style={{ background: '#120a1e', borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem', fontWeight: 600 }}>The Decision That Almost Did Not Happen</p>
            <p style={{ fontSize: '1.02rem', fontWeight: 700, color: '#fff', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, marginBottom: '1rem' }}>
              A senior product stakeholder wanted to force all users onto the new interface after a 6-month transition window. Veterans would have to adapt or leave.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              The business case was understandable — maintaining two interaction layers indefinitely adds engineering complexity. But the customers at risk were banks and insurers running mission-critical systems. Losing even two enterprise accounts would have cost more than years of dual-layer maintenance.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              I presented the churn risk analysis alongside a long-term roadmap showing how the dual-layer approach would become a single layer naturally as veteran users retired over time. The forced migration was cancelled.
            </p>
            <div style={{ background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)`, borderRadius: '8px', padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: '0.88rem', color: accent, lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                Design decisions that affect long-standing enterprise relationships require business evidence, not just UX rationale. I learned to bring both to every stakeholder conversation.
              </p>
            </div>
          </div>

          {/* Documented Design Principle */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}><FlatIcon name="rule" size={22} color="#94a3b8" /></span>
            <div>
              <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.4rem', fontWeight: 600 }}>DOCUMENTED DESIGN PRINCIPLE</p>
              <p style={{ fontSize: '0.95rem', color: '#1A1A1A', lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                "Never remove what users rely on. Add alongside, never instead of. Retirement happens naturally — it should never be forced."
              </p>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif", margin: '0.5rem 0 0' }}>
                Applied to every subsequent feature deprecation decision across the platform.
              </p>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Business Impact ─────────────────────────────────────────── */}
      <Accordion label="📈 Business Impact" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>Measurable Results</h3>
          <div style={T.impactGroups}>
            {IMPACT_GROUPS.map((group) => (
              <div key={group.groupLabel} style={T.impactGroup}>
                <div style={{...T.impactGroupLabel, display:"flex", alignItems:"center", gap:"0.5rem"}}><FlatIcon name={group.groupIcon || "bolt"} size={14} color="currentColor" />{group.groupLabel}</div>
                <div style={T.impactStats} className="cs-impact-stats">
                  {group.stats.map((s) => (
                    <div key={s.desc} style={T.impactStat}>
                      <span style={{ ...T.impactNum, color: accent }}>{s.num}</span>
                      <span style={T.impactDesc}>
                        {s.desc}
                        {s.sub && <small style={{ display: 'block', fontSize: '0.68rem', color: '#94a3b8', marginTop: '2px' }}>{s.sub}</small>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── User Feedback ───────────────────────────────────────────── */}
      <Accordion label="💬 User Feedback" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>In Their Own Words</h3>
          <div style={T.quotesGrid} className="cs-quotes-grid">
            {[
              'It looks like a completely different product — but all my shortcuts still work. I did not have to relearn anything. That is genuinely impressive.',
              'We cancelled two third-party UI library subscriptions on the day we went live. That alone paid for the upgrade project in under six months.',
              'Our junior developers actually want to use it now. We went from struggling to recruit Uniface talent to having people ask to work on it.',
            ].map((q, i) => (
              <div key={i} style={T.userQuote}>{q}</div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── Key Learnings ───────────────────────────────────────────── */}
      <Accordion label="💡 Key Learnings &amp; Reflection" accent={accent}>
        <div style={{ ...T.section, background: '#f8fafc' }} className="cs-section">
          <h3 style={T.sectionTitle}>What We Learned</h3>
          <div style={T.learningsGrid} className="cs-learnings-grid">
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What Worked</h4>
              <ul style={T.caseList}>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}>
                  <FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />
                  <strong>Additive modernisation</strong> — layering new on top of old, never removing, preserved veteran trust while delivering the modern feel new users expected
                </li>
                {[
                  'Adopting Fluent Design System as the foundation gave the team a shared visual language and dramatically reduced design decision overhead',
                  'Building the widget framework for extensibility from day one — customers became design system contributors rather than workaround creators',
                  'Living documentation reduced design review requests by 70% — teams in different time zones could ship consistently without waiting for feedback',
                ].map((item) => (
                  <li key={item} style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />{item}</li>
                ))}
              </ul>
            </div>
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What I Would Change</h4>
              <ul style={T.caseList}>
                {[
                  'Involve veteran users in token naming — early naming conventions felt too abstract for engineers who thought in legacy component terms',
                  'Define the accessibility standard earlier — retrofitting WCAG AA compliance onto 40 components mid-project added two months to the timeline',
                  'Build the documentation hub in parallel, not after — teams started building before the docs were ready and created inconsistencies that took months to correct',
                ].map((item) => (
                  <li key={item} style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Core Takeaway */}
          <div style={T.takeaway}>
            <FlatIcon name="star" size={24} color={accent} style={T.takeawayIcon} />
            <div>
              <strong style={T.takeawayTitle}>Core Takeaway</strong>
              <p style={T.takeawayText}>Modernising a platform that people have relied on for 40 years is not a design challenge — it is a trust challenge. The visual system was secondary. The real work was understanding exactly which aspects of the legacy experience represented genuine user value and protecting them with the same care as any new feature.</p>
            </div>
          </div>
        </div>
      </Accordion>

    </div>
  );
}
